import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Modal, FooterButton } from 'Core/components';
import { AppModal, MODAL_TYPES } from 'Feature/components/app-modals';
import { Printer } from 'react-feather';
import { LocationService } from '../../service/api/location-service';
import { ProductionService } from '../../service/api/production-service';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';

const LocationPrintModal = ({ showModal, onCloseModal }) => {
    const dispatch = useDispatch();
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [itemsOptions, setItemsOptions] = useState([]);
    const [printerOptions, setPrinterOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (showModal) {
            fetchInitialData();
            setSelectedItems([]);
            setSelectedPrinter(null);
        }
    }, [showModal]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [locationsRes, printersRes] = await Promise.all([
                LocationService.getAllLocations({}),
                ProductionService.getPrinters()
            ]);

            if (locationsRes.status === 200) {
                const locations = locationsRes.response || [];
                setItemsOptions(locations.map(loc => ({
                    value: loc.id,
                    label: loc.name || loc.description || 'Unknown Location'
                })));
            }

            if (printersRes.status === 200) {
                const printers = printersRes.response || [];
                setPrinterOptions(printers.map(printer => ({
                    value: printer.printerId,
                    label: printer.printerName
                })));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            dispatch(showToast({
                message: 'Failed to load print options',
                type: 'danger',
            }));
        } finally {
            setLoading(false);
        }
    };

    const [searchInput, setSearchInput] = useState('');



    const [isPrinting, setIsPrinting] = useState(false);

    const handlePrint = async () => {
        if (!selectedPrinter || selectedItems.length === 0) return;

        setIsPrinting(true);
        try {
            const request = {
                LocationIds: selectedItems.map(item => item.value),
                PrinterId: selectedPrinter.value
            };

            const response = await LocationService.printLocations(request);

            if (response.status === 200) {
                dispatch(showToast({
                    message: 'Print job initiated successfully',
                    type: 'success',
                }));
                onCloseModal();
            } else {
                throw new Error(response.message || 'Failed to initiate print job');
            }
        } catch (error) {
            console.error("Print error:", error);
            dispatch(showToast({
                message: 'Failed to initiate print job',
                type: 'danger',
            }));
        } finally {
            setIsPrinting(false);
        }
    };

    const body = () => (
        <div className="row">
            <div className="col-12 mb-3">
                <label className="form-label">Select Locations</label>
                <div className="d-flex align-items-center mb-2 justify-content-between">
                    <div>
                        <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                const visibleOptions = searchInput
                                    ? itemsOptions.filter(item => item.label.toLowerCase().includes(searchInput.toLowerCase()))
                                    : itemsOptions;

                                const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every(option =>
                                    selectedItems.some(item => item.value === option.value)
                                );

                                if (allVisibleSelected) {
                                    const newSelected = selectedItems.filter(item =>
                                        !visibleOptions.some(visible => visible.value === item.value)
                                    );
                                    setSelectedItems(newSelected);
                                } else {
                                    const newSelected = [...selectedItems];
                                    visibleOptions.forEach(option => {
                                        if (!newSelected.some(item => item.value === option.value)) {
                                            newSelected.push(option);
                                        }
                                    });
                                    setSelectedItems(newSelected);
                                }
                            }}
                            type="button"
                            disabled={loading || itemsOptions.length === 0}
                        >
                            {(() => {
                                const visibleOptions = searchInput
                                    ? itemsOptions.filter(item => item.label.toLowerCase().includes(searchInput.toLowerCase()))
                                    : itemsOptions;
                                const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every(option =>
                                    selectedItems.some(item => item.value === option.value)
                                );
                                return allVisibleSelected ? 'Deselect Visible' : 'Select Visible';
                            })()}
                        </button>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setSelectedItems([])}
                            type="button"
                            disabled={loading || selectedItems.length === 0}
                        >
                            Clear All
                        </button>
                    </div>
                    <small className="text-muted">
                        {selectedItems.length} of {itemsOptions.length} selected
                    </small>
                </div>
                <Select
                    isMulti
                    options={itemsOptions}
                    value={selectedItems}
                    onChange={setSelectedItems}
                    onInputChange={(value) => setSearchInput(value)}
                    placeholder={loading ? "Loading locations..." : "Search and select locations..."}
                    isSearchable
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isDisabled={loading}
                    components={{
                        Option: (props) => {
                            return (
                                <div style={{ display: 'flex', padding: '8px 12px', cursor: 'pointer' }} onClick={() => props.selectOption(props.data)}>
                                    <input
                                        type="checkbox"
                                        checked={props.isSelected}
                                        onChange={() => null}
                                        style={{ marginRight: '8px' }}
                                    />
                                    {props.label}
                                </div>
                            );
                        }
                    }}
                    styles={{
                        multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#e9ecef',
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            maxHeight: '120px',
                            overflowY: 'auto',
                        }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999
                        })
                    }}
                />
            </div>

            <div className="col-12 mb-3">
                <label className="form-label">Select Printer</label>
                <Select
                    options={printerOptions}
                    value={selectedPrinter}
                    onChange={setSelectedPrinter}
                    placeholder={loading ? "Loading printers..." : "Select a printer..."}
                    isDisabled={loading}
                />
            </div>
        </div>
    );

    const footer = () => (
        <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={onCloseModal} disabled={isPrinting}>
                Cancel
            </button>
            <button
                className="btn btn-primary d-flex align-items-center"
                onClick={handlePrint}
                disabled={selectedItems.length === 0 || !selectedPrinter || loading || isPrinting}
            >
                {isPrinting ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Printing...
                    </>
                ) : (
                    <>
                        <Printer size={16} className="me-2" />
                        Print
                    </>
                )}
            </button>
        </div>
    );

    return (
        <Modal
            id="location-print-modal"
            show={showModal}
            size="md"
            title="Print Locations"
            body={body}
            footer={footer}
            handleClose={onCloseModal}
        />
    );
};

LocationPrintModal.propTypes = {
    showModal: PropTypes.bool,
    onCloseModal: PropTypes.func,
};

export default AppModal(LocationPrintModal);
